import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Image,
  Button,
  Text,
  Flex,
  useColorModeValue,
  Collapse,
  Input,
  FormControl,
  FormLabel,
  VStack,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { useColors } from './colors';

import { updateMarketplaceApi } from 'features/marketplaces/marketplaces.api'; 

const MarketplaceCard = ({
  id,
  logo,
  name,
  storeCount,
  clientId: initialClientId,
  clientSecret: initialClientSecret,
  runame: initialRuname,
  isConfigValid,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clientId, setClientId] = useState(initialClientId);
  const [clientSecret, setClientSecret] = useState(initialClientSecret);
  const [runame, setRuname] = useState(initialRuname);

  const dispatch = useDispatch();
  const { updateMarketplaceStatus } = useSelector((state) => state.marketplaces);
  const isLoading = updateMarketplaceStatus === 'pending';

  const {
    backgroundCardColor,
    borderColor,
    textColor,
    inputBackground,
    getStatusColors,
  } = useColors();

  const configStatusColors = getStatusColors(isConfigValid);
  const storeStatusColors = getStatusColors(storeCount > 0);

  const handleSave = async () => {
    const result = await dispatch(
      updateMarketplaceApi({
        id,
        data: {
          configurations: [
            { key: 'clientId', value: clientId },
            { key: 'clientSecret', value: clientSecret },
            { key: 'runame', value: runame },
          ],
        },
      }),
    );

    if (updateMarketplaceApi.fulfilled.match(result)) {
      setIsOpen(false);
    }
  };

  return (
    <Box
      bg={backgroundCardColor}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="20px"
      p={6}
      shadow="md"
      transition="all 0.2s"
      _hover={{ shadow: 'lg' }}
    >
      <Flex direction="column" height="100%" justify="space-between">
        <Box>
          <Image src={logo} alt={`${name} Logo`} maxH="50px" mb={4} objectFit="contain" />
          <Button
            colorScheme="blue"
            size="md"
            width="full"
            mb={4}
            onClick={() => setIsOpen(!isOpen)}
            rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          >
            Settings
          </Button>

          <Collapse in={isOpen} animateOpacity>
            <VStack spacing={4} mb={4} bg={inputBackground} p={4} borderRadius="md">
              <FormControl>
                <FormLabel fontSize="sm" color={textColor}>
                  Client ID
                </FormLabel>
                <Input
                  placeholder="Enter Client ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  size="sm"
                  isDisabled={isLoading}
                  color={textColor}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" color={textColor}>
                  Client Secret
                </FormLabel>
                <Input
                  placeholder="Enter Client Secret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  size="sm"
                  isDisabled={isLoading}
                  color={textColor}
                />
              </FormControl>

              {name === 'ebay' && (
                <FormControl>
                  <FormLabel fontSize="sm" color={textColor}>
                    Runame
                  </FormLabel>
                  <Input
                    placeholder="Enter Runame"
                    value={runame}
                    onChange={(e) => setRuname(e.target.value)}
                    size="sm"
                    isDisabled={isLoading}
                    color={textColor}
                  />
                </FormControl>
              )}

              <Button
                colorScheme="green"
                size="sm"
                width="full"
                onClick={handleSave}
                isLoading={isLoading}
                loadingText="Saving..."
              >
                Save
              </Button>
            </VStack>
          </Collapse>
        </Box>

        <Flex gap={4}>
          <Box
            bg={configStatusColors.bg}
            px={4}
            py={2}
            borderRadius="full"
            flex={1}
            border="1px solid"
            borderColor={useColorModeValue('transparent', configStatusColors.color)}
          >
            <Text
              color={configStatusColors.color}
              fontSize="sm"
              fontWeight="medium"
              textAlign="center"
            >
              {isConfigValid ? 'Dev Configs Valid' : 'Dev Configs Invalid'}
            </Text>
          </Box>

          <Box
            bg={storeStatusColors.bg}
            px={4}
            py={2}
            borderRadius="full"
            flex={1}
            border="1px solid"
            borderColor={useColorModeValue('transparent', storeStatusColors.color)}
          >
            <Text
              color={storeStatusColors.color}
              fontSize="sm"
              fontWeight="medium"
              textAlign="center"
            >
              Stores Connected: {storeCount}
            </Text>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

export default MarketplaceCard;
